import React, { memo, useMemo, useCallback } from 'react';
import { createUseStyles, Text, Icon, useTheme } from '@Sage/hoi-poi-ui';
import classnames from 'classnames';
import { useWeb5Context } from 'lib/Web5Context';
import { CHART_TYPES, NOT_CHART_TYPES } from '../../../../constants';

import styles from './styles';

const useStyles = createUseStyles(styles, 'StepsInfo');

const StepsInfo = memo(({ data, onReturnToMainTab }) => {
    const theme = useTheme();
    const classes = useStyles();
    const { t, getWeb4EntityFromString } = useWeb5Context();

    const chartPlaceholder = useMemo(() => {
        const chartTypes = {
            [CHART_TYPES.bar]: 'verticalBarChartColor',
            [CHART_TYPES.hBar]: 'horizontalBarChartColor',
            [CHART_TYPES.line]: 'linesChartColor',
            [CHART_TYPES.pie]: 'pieChartColor',
            [CHART_TYPES.area]: 'areaChartColor',
            [CHART_TYPES.doughnut]: 'radialBarChartColor',
            [NOT_CHART_TYPES.kpi]: 'counterChartColor',
            [NOT_CHART_TYPES.table]: 'tableChartColor',
            [NOT_CHART_TYPES.map]: 'mapChartColor',
        };

        return (
            <div className={classes.chartType}>
                <Icon className={classes.chartTypeIcon} name={chartTypes[data?.chartType]} />
            </div>
        );
    }, [data?.chartType, classes.chartType, classes.chartTypeIcon]);

    const getIcon = useCallback(
        (entity) => {
            const specialEntity = {
                internetEmails: 'email',
                phoneCalls: 'localPhone',
                salesorders: 'salesOrders',
            };

            let finalName = entity;
            if (specialEntity[entity]) finalName = specialEntity[entity];
            return <Icon name={finalName} color={theme.colors.semantic.info400} />;
        },
        [theme.colors],
    );

    const entityPlaceholder = useMemo(() => {
        const hasData = !!data?.entity?.value;
        const entity = getWeb4EntityFromString(data?.entity?.value);

        return (
            <div className={classnames(classes.box, { [classes.incomplete]: !hasData })}>
                <div className={classes.boxLeft}>
                    {hasData && entity?.trueName && getIcon(entity.trueName)}
                    {!hasData && <Icon name="addPlus" />}
                </div>
                <div className={classes.boxRight}>
                    {hasData && <Text isTruncated>{data?.entity?.label || ''}</Text>}
                    {!hasData && (
                        <Text color={!hasData ? theme.colors.textLight.secondary : ''}>
                            {t('label_forcebi_crud_placeholder_select_entity')}
                        </Text>
                    )}
                </div>
            </div>
        );
    }, [
        data,
        classes.box,
        classes.boxLeft,
        classes.boxRight,
        classes.incomplete,
        t,
        getWeb4EntityFromString,
        getIcon,
        theme,
    ]);

    const yPlaceholder = useMemo(() => {
        const hasData = !!data?.yAxis;
        let content = (
            <Text color={!hasData ? theme.colors.textLight.secondary : ''}>
                {t('label_select_y_axis_value')}
            </Text>
        );
        if (hasData) {
            content = <Text>{data?.yAxis?.label || ''}</Text>;
        }

        const iconName = hasData ? 'yAxisColor' : 'yAxis';

        return (
            <div
                className={classnames(classes.box, {
                    [classes.incomplete]: !hasData,
                })}
            >
                <div className={classes.boxLeft}>
                    <Icon name={iconName} />
                </div>
                <div className={classes.boxRight}>{content}</div>
            </div>
        );
    }, [data?.yAxis, classes.box, classes.boxLeft, classes.boxRight, classes.incomplete, t, theme]);

    const xPlaceholder = useMemo(() => {
        const hasData = !!data?.xAxis;
        let content = (
            <Text color={!hasData ? theme.colors.textLight.secondary : ''}>
                {t('label_select_x_axis_value')}
            </Text>
        );

        if (hasData) {
            content = <Text>{data?.xAxis?.label || ''}</Text>;
        }

        const iconName = data?.xAxis ? 'xAxisColor' : 'xAxis';

        return (
            <div
                className={classnames(classes.box, {
                    [classes.incomplete]: !hasData,
                })}
            >
                <div className={classes.boxLeft}>
                    <Icon name={iconName} />
                </div>
                <div className={classes.boxRight}>{content}</div>
            </div>
        );
    }, [data?.xAxis, classes.box, classes.boxLeft, classes.boxRight, classes.incomplete, t, theme]);

    const shouldRenderXAxisAggregationDatePlaceholder = useMemo(() => {
        if (data?.xAxis?.isDateField) return true;
        return false;
    }, [data?.xAxis]);

    const xAxisAggregationDatePlaceholder = useMemo(() => {
        const hasData = !!data?.xAxisAggregationDate;
        let content = (
            <Text color={!hasData ? theme.colors.textLight.secondary : ''}>
                {t('label_select_aggregation_date')}
            </Text>
        );

        if (hasData) {
            content = <Text>{data?.xAxisAggregationDate?.label || ''}</Text>;
        }
        return (
            <div className={classnames(classes.box, { [classes.incomplete]: !hasData })}>
                <div className={classes.boxLeft}>
                    <Icon name="dateRange" />
                </div>
                <div className={classes.boxRight}>{content}</div>
            </div>
        );
    }, [
        data?.xAxisAggregationDate,
        classes.box,
        classes.boxLeft,
        classes.incomplete,
        classes.boxRight,
        t,
        theme,
    ]);

    const yAxisAggregationPlaceholder = useMemo(() => {
        const hasData = !!data?.yAxisAggregation;
        let content = (
            <Text color={!hasData ? theme.colors.textLight.secondary : ''}>
                {t('label_select_aggregation_method')}
            </Text>
        );

        if (hasData) {
            content = <Text>{data?.yAxisAggregation?.label || ''}</Text>;
        }

        return (
            <div className={classnames(classes.box, { [classes.incomplete]: !hasData })}>
                <div className={classes.boxLeft}>
                    <Icon name="percentage" />
                </div>
                <div className={classes.boxRight}>{content}</div>
            </div>
        );
    }, [
        data?.yAxisAggregation,
        classes.box,
        classes.boxLeft,
        classes.incomplete,
        classes.boxRight,
        t,
        theme,
    ]);

    const line = useMemo(() => {
        return <div className={classes.line} />;
    }, [classes.line]);

    return (
        <div className={classes.root}>
            {entityPlaceholder}
            {line}
            {yPlaceholder}
            {line}
            {xPlaceholder}
            {line}
            {shouldRenderXAxisAggregationDatePlaceholder && xAxisAggregationDatePlaceholder}
            {shouldRenderXAxisAggregationDatePlaceholder && line}
            {yAxisAggregationPlaceholder}
            {line}
            {chartPlaceholder}
        </div>
    );
});

export default StepsInfo;
